import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { placeCreatedRowFirst } from "utils/helpers";

import {
  SuperModelForm,
  SuperModelListMeta,
  SuperModelManufacturerFilter,
  SuperModelManufacturerFilterOption,
  SuperModelRow,
  SuperModelUpdate,
} from "../types";
import {
  initialFormDropDownState,
  initialSuperModelForm,
  initialState,
} from "./initialState";

export const sliceKey = "superModelState";

type SuperModelListSuccessPayload = {
  data: SuperModelRow[];
  meta: SuperModelListMeta;
};

type FormDropDownSuccessPayload = {
  data: SuperModelManufacturerFilterOption[];
  meta: SuperModelListMeta;
};

const superModelSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getSuperModelsList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getSuperModelsListSuccess(
      state,
      action: PayloadAction<SuperModelListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getSuperModelsListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<SuperModelListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setManufacturerFilter(
      state,
      action: PayloadAction<SuperModelManufacturerFilter>
    ) {
      state.manufacturerFilter = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialSuperModelForm;
      state.formDropDown = { ...initialFormDropDownState };
    },

    getFormDropDownList(state) {
      state.formDropDown = {
        ...initialFormDropDownState,
        perPage: state.formDropDown.perPage,
        loading: true,
      };
    },

    getFormDropDownListSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>,
    ) {
      state.formDropDown = {
        ...state.formDropDown,
        items: action.payload.data,
        meta: action.payload.meta,
        page: action.payload.meta.currentPage,
        loading: false,
      };
    },

    getFormDropDownListError(state, action: PayloadAction<string>) {
      state.formDropDown.loading = false;
      state.error = action.payload;
    },

    getFormDropDownNextPage(state) {
      const dropDown = state.formDropDown;
      if (
        dropDown.loading ||
        dropDown.meta.currentPage >= dropDown.meta.totalPages
      ) {
        return;
      }
      dropDown.loading = true;
      dropDown.page = dropDown.meta.currentPage + 1;
    },

    getFormDropDownNextPageSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>,
    ) {
      const dropDown = state.formDropDown;
      const { data, meta } = action.payload;
      dropDown.loading = false;
      dropDown.items = dropDown.items.concat(data);
      dropDown.meta = meta;
      dropDown.page = meta.currentPage;
    },

    getFormDropDownNextPageError(state, action: PayloadAction<string>) {
      state.formDropDown.loading = false;
      state.error = action.payload;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof SuperModelForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialSuperModelForm;
    },

    addSuperModel(state) {
      state.saving = true;
      state.error = null;
    },

    addSuperModelSuccess(state, action: PayloadAction<SuperModelRow>) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialSuperModelForm;
      state.formDropDown = { ...initialFormDropDownState };
      state.error = null;
      state.list = placeCreatedRowFirst(state.list, action.payload);
    },

    addSuperModelError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateSuperModel(state, _action: PayloadAction<SuperModelUpdate>) {
      state.saving = true;
      state.error = null;
    },

    updateSuperModelSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateSuperModelError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    getSuperModelDetail(state, _action: PayloadAction<number>) {
      state.detailLoading = true;
      state.detailError = null;
      state.detail = null;
    },

    getSuperModelDetailSuccess(state, action: PayloadAction<SuperModelRow>) {
      state.detailLoading = false;
      state.detail = action.payload;
      state.detailError = null;
    },

    getSuperModelDetailError(state, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.detail = null;
      state.detailError = action.payload;
    },

    getManufacturerFilterOptions(state) {
      state.manufacturerOptionsLoading = true;
      state.manufacturerOptionsError = null;
    },

    getManufacturerFilterOptionsSuccess(
      state,
      action: PayloadAction<SuperModelManufacturerFilterOption[]>
    ) {
      state.manufacturerOptionsLoading = false;
      state.manufacturerOptions = Array.isArray(action.payload)
        ? action.payload
        : [];
      state.manufacturerOptionsError = null;
    },

    getManufacturerFilterOptionsError(state, action: PayloadAction<string>) {
      state.manufacturerOptionsLoading = false;
      state.manufacturerOptions = [];
      state.manufacturerOptionsError = action.payload;
    },

    deleteSuperModel(state, _action: PayloadAction<number>) {
      state.saving = true;
      state.error = null;
    },

    deleteSuperModelSuccess(state, action: PayloadAction<number>) {
      state.saving = false;
      state.error = null;
      state.list = state.list.filter(
        (item) => item.superModelId !== action.payload
      );
      if (state.detail?.superModelId === action.payload) {
        state.detail = null;
        state.detailLoading = false;
        state.detailError = null;
      }
    },

    deleteSuperModelError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions: superModelActions, reducer } = superModelSlice;
